import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { call } from '@/lib/api';
import { queryClient } from '@/lib/query';

// ============================================================================
// SINGLE SOURCE OF TRUTH: Define resources and their actions here
// All types and constants are automatically derived from this configuration
// ============================================================================
const PERMISSION_CONFIG = {
  invoices: ['read', 'write', 'delete', '*', 'send'],
  proposals: ['read', 'read-shop', 'write', 'send', 'publish', '*'],
  offers: [
    'read',
    'write',
    'send',
    'delete',
    'fulfill',
    'approve-split',
    'issue-installment-all',
    '*',
  ],
  services: ['read', 'write', '*'],
  customers: ['read', 'write', '*'],
  contacts: ['read', 'read-all', 'write', 'assign', 'delete', '*'],
  documents: ['read', 'write', '*'],
  agents: ['read', 'write', '*'],
  monday: ['read', 'write', '*'],
  stats: ['read', '*'],
  rbac: ['read', 'write', '*'],
  dashboard: ['read', '*'],
  companies: ['read', 'write', '*'],
  renewals: ['read', 'write', '*'],
  leads: ['read', 'read-all', 'write', 'assign', 'delete', '*'],
  payers: ['read', 'write', '*'],
} as const;

export const INVOICING_COMPANIES_ALL = 'invoicing-companies:*' as const;
export const PAYERS_ALL = 'payers:*' as const;
export const PROPOSALS_READ_SHOP = 'proposals:read-shop' as const;

// Hyphenated resources cannot be expressed in PERMISSION_CONFIG because the
// generated constant keys would contain a hyphen (see `invoicing-companies`).
export const REFERRING_AGENTS_READ = 'referring-agents:read' as const;
export const REFERRING_AGENTS_WRITE = 'referring-agents:write' as const;
export const REFERRING_AGENTS_ALL = 'referring-agents:*' as const;

// ============================================================================
// Derived types (automatically generated from config)
// ============================================================================
type PermissionConfig = typeof PERMISSION_CONFIG;
type ResourceKey = keyof PermissionConfig;
type ActionForResource<R extends ResourceKey> = PermissionConfig[R][number];

// Generate Permission type - full autocomplete for all valid permissions
export type Permission =
  | {
      [R in ResourceKey]: `${R}:${ActionForResource<R>}`;
    }[ResourceKey]
  | '*:*'
  | 'invoicing-companies:*'
  | 'payers:*'
  | 'referring-agents:read'
  | 'referring-agents:write'
  | 'referring-agents:*';

// ============================================================================
// Auto-generated PERMISSIONS constants (preserves full type safety)
// ============================================================================

// Helper to create permission constant entries for a resource
type CreatePermissionEntries<
  R extends ResourceKey,
  Actions extends readonly string[],
> = {
  [A in Actions[number] as A extends '*'
    ? `${Uppercase<R>}_ALL`
    : `${Uppercase<R>}_${Uppercase<A>}`]: `${R}:${A}`;
};

// Merge all resource permissions into a single object type
type PermissionsObject = CreatePermissionEntries<
  'invoices',
  typeof PERMISSION_CONFIG.invoices
> &
  CreatePermissionEntries<'proposals', typeof PERMISSION_CONFIG.proposals> &
  CreatePermissionEntries<'offers', typeof PERMISSION_CONFIG.offers> &
  CreatePermissionEntries<'services', typeof PERMISSION_CONFIG.services> &
  CreatePermissionEntries<'customers', typeof PERMISSION_CONFIG.customers> &
  CreatePermissionEntries<'contacts', typeof PERMISSION_CONFIG.contacts> &
  CreatePermissionEntries<'documents', typeof PERMISSION_CONFIG.documents> &
  CreatePermissionEntries<'agents', typeof PERMISSION_CONFIG.agents> &
  CreatePermissionEntries<'monday', typeof PERMISSION_CONFIG.monday> &
  CreatePermissionEntries<'stats', typeof PERMISSION_CONFIG.stats> &
  CreatePermissionEntries<'rbac', typeof PERMISSION_CONFIG.rbac> &
  CreatePermissionEntries<'dashboard', typeof PERMISSION_CONFIG.dashboard> &
  CreatePermissionEntries<'companies', typeof PERMISSION_CONFIG.companies> &
  CreatePermissionEntries<'renewals', typeof PERMISSION_CONFIG.renewals> &
  CreatePermissionEntries<'leads', typeof PERMISSION_CONFIG.leads> &
  CreatePermissionEntries<'payers', typeof PERMISSION_CONFIG.payers> & {
    ALL: '*:*';
  };

function buildPermissions(): PermissionsObject {
  const permissions: any = {
    ALL: '*:*',
  };

  for (const [resource, actions] of Object.entries(PERMISSION_CONFIG)) {
    for (const action of actions) {
      const key =
        action === '*'
          ? `${resource.toUpperCase()}_ALL`
          : `${resource.toUpperCase()}_${action.toUpperCase()}`;
      permissions[key] = `${resource}:${action}`;
    }
  }

  return permissions;
}

export const PERMISSIONS = buildPermissions();

class PermissionManager {
  private permissions: string[] = [];

  constructor(permissions: string[]) {
    this.permissions = permissions;
  }

  /**
   * Check if user has a specific permission
   * Supports wildcard matching:
   * - *:* matches everything
   * - invoices:* matches invoices:read, invoices:write, etc.
   * - invoices:read matches only invoices:read
   */
  hasPermission(required: string): boolean {
    const [requiredResource, requiredAction] = required.split(':');

    for (const permission of this.permissions) {
      const [resource, action] = permission.split(':');

      // Match if:
      // 1. *:* (full access)
      // 2. resource:* (wildcard action for resource)
      // 3. resource:action (exact match)
      if (
        (resource === '*' && action === '*') ||
        (resource === requiredResource && action === '*') ||
        (resource === requiredResource && action === requiredAction)
      ) {
        return true;
      }
    }

    return false;
  }

  /**
   * Check if user has ANY of the specified permissions
   */
  hasAnyPermission(required: string[]): boolean {
    return required.some((permission) => this.hasPermission(permission));
  }

  /**
   * Check if user has ALL of the specified permissions
   */
  hasAllPermissions(required: string[]): boolean {
    return required.every((permission) => this.hasPermission(permission));
  }

  /**
   * Get all user permissions
   */
  getPermissions(): string[] {
    return [...this.permissions];
  }
}

/**
 * Hook to fetch and manage user permissions
 */
export function usePermissions() {
  const {
    data: permissions = [],
    isLoading,
    error,
  } = useQuery(
    {
      queryKey: ['user-permissions'],
      queryFn: () =>
        call<string[]>(`user/permissions`, { method: 'GET' }).then((res) =>
          res.json(),
        ),
    },
    queryClient,
  );

  const permissionManager = useMemo(
    () => new PermissionManager(permissions),
    [permissions],
  );

  return {
    permissions,
    permissionManager,
    isLoading,
    error,
  };
}

/**
 * Hook to check a specific permission
 */
export function useHasPermission(permission: string): boolean {
  const { permissionManager } = usePermissions();
  return permissionManager.hasPermission(permission);
}

/**
 * Hook to check if user has ANY of the specified permissions
 */
export function useHasAnyPermission(permissions: string[]): boolean {
  const { permissionManager } = usePermissions();
  return permissionManager.hasAnyPermission(permissions);
}

export function useProposalAccess() {
  const { permissionManager } = usePermissions();
  const canReadAll = permissionManager.hasPermission(
    PERMISSIONS.PROPOSALS_READ,
  );
  const shopOnly =
    !canReadAll && permissionManager.hasPermission(PROPOSALS_READ_SHOP);
  const canSeeShop =
    permissionManager.hasPermission('proposals:publish') ||
    permissionManager.hasPermission(PROPOSALS_READ_SHOP);
  return { canView: canReadAll || shopOnly, shopOnly, canSeeShop };
}

/**
 * Hook to check if user has ALL of the specified permissions
 */
export function useHasAllPermissions(permissions: string[]): boolean {
  const { permissionManager } = usePermissions();
  return permissionManager.hasAllPermissions(permissions);
}

/**
 * What this user may see of an offer.
 *
 * These flags state the product rule, not an authorization fact: holding
 * `offers:delete` does not of itself mean "may read the legal side". The rule is that
 * a user who can fulfil an offer but not manage one - Operations - gets the restricted
 * view, and everyone else entitled to open the hub gets all of it. It is drawn from
 * the management capabilities a user positively holds rather than from a deny-list, so
 * a new permission cannot silently demote anyone.
 *
 * This governs presentation only. Every offer endpoint enforces its own access, and
 * issuing an installment is a bare permission check on the server.
 */
export interface OfferViewPolicy {
  /** The commercial and legal sides of the offer: the General and Legal tabs. */
  canViewCommercial: boolean;
  /** The whole Finance tab, rather than the payment schedule alone. */
  canViewFullFinance: boolean;
}

const OFFER_MANAGE_PERMISSIONS = [
  'offers:write',
  'offers:send',
  'offers:delete',
  'offers:approve-split',
];

export type OfferTabKey = 'general' | 'legal' | 'finance' | 'delivery';

/**
 * The offer hub's tabs, in the order they are shown - and therefore in priority order,
 * because an offer link carrying no tab opens the first one the viewer may see. This
 * is the single definition: it drives what renders, which URLs are accepted, and where
 * a tab-less link lands, so those three can never drift apart.
 */
const OFFER_TABS: {
  key: OfferTabKey;
  label: string;
  available: (policy: OfferViewPolicy) => boolean;
}[] = [
  { key: 'general', label: 'General', available: (p) => p.canViewCommercial },
  { key: 'legal', label: 'Legal', available: (p) => p.canViewCommercial },
  { key: 'finance', label: 'Finance', available: () => true },
  { key: 'delivery', label: 'Delivery', available: () => true },
];

export function useOfferTabs(): {
  tabs: { key: OfferTabKey; label: string }[];
  policy: OfferViewPolicy;
  isLoading: boolean;
} {
  const { permissionManager, isLoading } = usePermissions();
  const restricted =
    !permissionManager.hasAnyPermission(OFFER_MANAGE_PERMISSIONS) &&
    permissionManager.hasPermission('offers:fulfill');
  const policy: OfferViewPolicy = {
    canViewCommercial: !restricted,
    canViewFullFinance: !restricted,
  };
  return {
    tabs: OFFER_TABS.filter((t) => t.available(policy)).map(
      ({ key, label }) => ({ key, label }),
    ),
    policy,
    isLoading,
  };
}
